/**
 * CrmResearchPanel — AI prospect research for a lead. "Run research" calls /api/research (Claude +
 * web search) and stores the Property / Owner / Neighborhood / Fit Assessment sections on the lead
 * (Research table). Shows collapsible sections + a Fit badge. Requires name + full address.
 */
import { useEffect, useRef, useState } from 'react'
import Markdown from '../Markdown.jsx'
import { F } from './CrmModal.jsx'
import { runProspectResearch, saveLeadResearch, getLeadResearch } from '../../services/research.js'

const FIT_COLOR = {
  'Strong Fit': '#1f9d57', 'Good Fit': '#1f9d57', 'Potential Fit': '#9a6700',
  'Weak Fit': '#9a6700', 'Poor Fit': '#b3261e',
}

function fmtDate(v) {
  if (!v) return ''
  try { const d = new Date(v); return isNaN(d) ? String(v) : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) } catch { return String(v) }
}

export default function CrmResearchPanel({ lead }) {
  const [research, setResearch] = useState(null)   // null = loading
  const [running, setRunning] = useState(false)
  const [error, setError] = useState('')
  const [open, setOpen] = useState({})             // section type → expanded
  const loadedRef = useRef(false)

  useEffect(() => {
    if (loadedRef.current) return
    loadedRef.current = true
    const ids = lead.researchIds || []
    if (!ids.length) { setResearch([]); return }
    getLeadResearch(ids).then(setResearch).catch(() => setResearch([]))
  }, [lead])

  const fullName = `${lead.firstName || ''} ${lead.lastName || ''}`.trim() || lead.name
  const fullAddress = [lead.street, lead.city, lead.state].filter(Boolean).join(', ')

  // Name is always required; beyond that, any ONE identifying anchor is enough — an address is
  // no longer mandatory (plenty of leads only have a name plus an organization, an additional
  // contact, or a referral source to triangulate with).
  const anchors = []
  if (fullAddress) anchors.push('an address')
  if (lead.organization) anchors.push('an organization')
  if (lead.additionalContact) anchors.push('an additional contact')
  if (lead.referredByName) anchors.push('a referral source')
  const hasName = !!fullName
  const hasAnchor = anchors.length > 0
  const canRun = hasName && hasAnchor
  const hint = !hasName && !hasAnchor
    ? 'Add a name and at least one of: address, organization, additional contact, or referral source.'
    : !hasName
    ? 'Add a name to run research.'
    : !hasAnchor
    ? 'Add at least one of: address, organization, additional contact, or referral source.'
    : ''

  async function run() {
    if (running || !canRun) return
    setRunning(true); setError('')
    try {
      const res = await runProspectResearch({
        fullName,
        fullAddress: fullAddress || undefined,
        additionalContact: lead.additionalContact,
        organization: lead.organization,
        referredByName: lead.referredByName,
        projectDescription: lead.description,
      })
      if (res.error) { setError(res.error); return }
      if (!res.result) { setError('No research returned.'); return }
      const priorIds = (research || []).map(r => r.id)
      const created = await saveLeadResearch(lead.id, res.result, fullName, priorIds)
      setResearch(created)
    } catch (e) {
      setError((e && e.message) || 'Research failed.')
    } finally { setRunning(false) }
  }

  const fit = (research || []).find(r => r.type === 'Fit Assessment')
  const fitBadge = fit && fit.fitBadge
  const lastDate = research && research.length ? research[0].date : null

  return (
    <div style={S.wrap}>
      <div style={S.head}>
        <span style={S.headTitle}>Prospect research</span>
        {fitBadge && <span style={{ ...S.fit, background: (FIT_COLOR[fitBadge] || '#5a7884') + '1a', color: FIT_COLOR[fitBadge] || '#5a7884' }}>{fitBadge}</span>}
        <div style={{ flex: 1 }} />
        {lastDate && <span style={S.date}>Last run {fmtDate(lastDate)}</span>}
        <button
          style={{ ...F.btnGhost, padding: '5px 12px', opacity: canRun ? 1 : 0.5, cursor: canRun ? 'pointer' : 'not-allowed' }}
          onClick={run} disabled={running || !canRun}
          title={canRun ? 'Run AI research' : hint}
        >
          {running ? 'Researching…' : (research && research.length ? 'Re-run' : 'Run research')}
        </button>
      </div>

      {!canRun && (research === null || !research.length) && (
        <div style={S.muted}>{hint}</div>
      )}
      {error && <div style={S.error}>{error}</div>}

      {research === null ? (
        <div style={S.muted}>Loading…</div>
      ) : research.length === 0 ? (
        !running && canRun && <div style={S.muted}>Pull property, owner, neighborhood, and fit in one click.</div>
      ) : (
        <div style={{ marginTop: 8 }}>
          {research.map(sec => {
            const isOpen = open[sec.type] !== false   // default expanded
            return (
              <div key={sec.id} style={S.section}>
                <button style={S.secHead} onClick={() => setOpen(o => ({ ...o, [sec.type]: isOpen ? false : true }))}>
                  <span style={S.secTitle}>{sec.type}</span>
                  <span style={S.secArrow}>{isOpen ? '▾' : '▸'}</span>
                </button>
                {isOpen && <div style={S.secBody}><Markdown text={sec.content} /></div>}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const S = {
  wrap: { marginTop: 16, paddingTop: 14, borderTop: '0.5px solid var(--aj-border)' },
  head: { display: 'flex', alignItems: 'center', gap: 10 },
  headTitle: { fontFamily: '"Montserrat", sans-serif', fontSize: 10.5, fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--aj-text-mid)' },
  fit: { fontFamily: '"DM Sans", sans-serif', fontSize: 11, fontWeight: 600, borderRadius: 20, padding: '2px 9px' },
  date: { fontFamily: '"DM Mono", monospace', fontSize: 11, color: 'var(--aj-text-mid)' },
  muted: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text-mid)', fontStyle: 'italic', marginTop: 8 },
  error: { fontFamily: '"DM Sans", sans-serif', fontSize: 12, color: '#b3261e', marginTop: 8 },
  section: { borderBottom: '0.5px solid var(--aj-border)' },
  secHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '9px 0' },
  secTitle: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text)' },
  secArrow: { color: 'var(--aj-text-mid)', fontSize: 11 },
  secBody: { fontFamily: '"DM Sans", sans-serif', fontSize: 12.5, color: 'var(--aj-text)', lineHeight: 1.5, padding: '0 0 12px' },
}
